import React, { useState, useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie, Doughnut } from "react-chartjs-2";
import * as Components from "../../../components";
import { NativeSvgIcon } from "../../Admin/Shared";
import "../style/Dashboard.scss";
import moment from "moment";
import { useGetDriverScoresQuery, useGetAreasQuery } from "../services";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

export const DriverBehaviorDashboard = () => {
  // Filter state (temporary values before applying)
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedDate, setSelectedDate] = useState({ startTime: moment(), endTime: moment() });
  const [showCalender, setShowCalender] = useState(false);
  const [resetCount, setResetCount] = useState(0);

  // Applied filter state (actual values used in API query)
  const [appliedArea, setAppliedArea] = useState("");
  const [appliedGroup, setAppliedGroup] = useState("");
  const [appliedDate, setAppliedDate] = useState({ startTime: moment(), endTime: moment() });

  // Fetch areas data
  const { data: areasData } = useGetAreasQuery({ page: 0, per_page: 1000 });

  // API query parameters - using applied filters
  const queryParams = useMemo(() => ({
    page: 0,
    per_page: 10,
    start_date: appliedDate.startTime.format("YYYY-MM-DD"),
    end_date: appliedDate.endTime.format("YYYY-MM-DD"),
    ...(appliedArea && { area: appliedArea }),
    ...(appliedGroup && { group: appliedGroup }),
  }), [appliedArea, appliedGroup, appliedDate]);

  // Fetch driver scores data
  const { data: driverScoresData, isLoading, error } = useGetDriverScoresQuery(queryParams);

  // Handle apply filters
  const handleApplyFilters = () => {
    setAppliedArea(selectedArea);
    setAppliedGroup(selectedGroup);
    setAppliedDate(selectedDate);
  };

  // Extract data from API response
  const apiData = useMemo(() => {
    if (!driverScoresData?.data?.[0]) {
      return null;
    }
    return driverScoresData.data[0];
  }, [driverScoresData]);

  // Helper function to sum chart data counts
  const sumChartData = (chartDataArray) => {
    if (!chartDataArray || !Array.isArray(chartDataArray)) return 0;
    return chartDataArray.reduce((sum, [, count]) => sum + count, 0);
  };

  // Process chart data for each metric
  const getMetricChartData = (metricId) => {
    if (!apiData?.chart_data) {
      return { data: [50, 30, 20] }; // Default data
    }

    const chartData = apiData.chart_data;
    let metricData;

    // Map UI metric IDs to API chart_data keys
    switch (metricId) {
      case "harsh-acceleration":
        metricData = chartData.harsh_acceleration;
        break;
      case "harsh-braking":
        metricData = chartData.harsh_breaking; // Note: API has typo "breaking"
        break;
      case "idle":
        metricData = chartData.idling;
        break;
      case "speeding":
        metricData = chartData.over_speed;
        break;
      case "harsh-turn":
        // harsh-turn not in API, return default
        return { data: [50, 30, 20] };
      default:
        return { data: [50, 30, 20] };
    }

    if (!metricData || !Array.isArray(metricData) || metricData.length === 0) {
      return { data: [50, 30, 20] }; // Default data if no metric data
    }

    // Calculate total count
    const totalCount = sumChartData(metricData);

    // For now, using mock distribution - in real scenario, you'd need
    // actual categorization of normal/over/excessive from API
    return {
      data: [
        Math.floor(totalCount * 0.5),  // Normal Speed
        Math.floor(totalCount * 0.3),  // Over Speed
        Math.floor(totalCount * 0.2),  // Excessive Speed
      ],
    };
  };

  const pieChartLegends = {
    normalSpeed: {
      label: "Normal Speed",
      color: "#36a2ec"
    },
    overSpeed: {
      label: "Over Speed",
      color: "#ffce56"
    },
    excessiveSpeed: {
      label: "Excessive Speed",
      color: "#ff6484"
    },
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
  };

  const driverMetrics = [
    { id: "harsh-acceleration", label: "HARSH ACCELERATION" },
    { id: "harsh-braking", label: "HARSH BRAKING" },
    { id: "harsh-turn", label: "HARSH TURN" },
    { id: "idle", label: "IDLE" },
    { id: "speeding", label: "SPEEDING" },
  ];

  // Donut chart configuration
  const createDonutData = (percentage, color) => ({
    datasets: [
      {
        data: [percentage, 100 - percentage],
        backgroundColor: [color, "#E8F5E9"],
        borderWidth: 0,
      },
    ],
  });

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "70%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  // Driver score categories with API data
  const driverScoreCategories = useMemo(() => [
    {
      id: "green-score",
      label: "DRIVER IN A GREEN SCORE (100%-90%)",
      percentage: apiData?.green_score_precentage || 0, // Note: API has typo "precentage"
      color: "#76a000",
    },
    {
      id: "orange-score",
      label: "DRIVER IN A ORANGE SCORE (89%-70%)",
      percentage: apiData?.orange_score_percentage || 0,
      color: "#ff9800",
    },
    {
      id: "red-score",
      label: "DRIVER IN A RED SCORE (<70%)",
      percentage: apiData?.red_score_percentage || 0,
      color: "#f44336",
    },
  ], [apiData]);

  const handleDateRange = (startTime, endTime) => {
    setSelectedDate((prevState) => ({
      ...prevState,
      startTime,
      endTime,
    }));
    setResetCount((i) => i + 1);
  };

  return (
    <div className="driver-behavior-dashboard">
      <div className="dashboard-content">
        <div className="dashboard-filters">
        {/* Left side filters */}
        <div className="filters-left">
          <div className="filter-item">
            <Components.QICustomSelect
              label="Area"
              value={selectedArea}
              onChange={(value) => setSelectedArea(value)}
            >
              <li value="">All Areas</li>
              {areasData?.data?.map((area) => (
                <li key={area.id} value={area.id}>
                  {area.name}
                </li>
              ))}
            </Components.QICustomSelect>
          </div>

          <div className="filter-item">
            <Components.QICustomSelect
              label="Group Name"
              value={selectedGroup}
              onChange={(value) => setSelectedGroup(value)}
            >
              <li value="">Select Group</li>
              <li value="group-1">Group 1</li>
              <li value="group-2">Group 2</li>
            </Components.QICustomSelect>
          </div>

          <div className="filter-item">
            <Components.QIDateRangePicker
              start={selectedDate.startTime}
              end={selectedDate.endTime}
              dateFormat={"D MMM, YYYY"}
              label={"Date Range"}
              maxDate={moment()}
              onChange={handleDateRange}
              setShowCalender={setShowCalender}
              className=""
              resetCount={resetCount}
            />
          </div>

          <div className="filter-item apply-now">
            <button
              className="apply-filter-btn"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
          </div>
        </div>

        {/* Right side: range selector and summary cards */}
        <div className="filters-right">
          <div className="summary-cards">
            <div className="summary-card blue">
              <div className="card-icon">
                <NativeSvgIcon name="user" />
              </div>
              <div className="card-content">
                <h3>BEST DRIVER</h3>
                <p className="card-value">N/A</p>
              </div>
            </div>

            <div className="summary-card green">
              <div className="card-icon">
                <NativeSvgIcon name="vehicle" />
              </div>
              <div className="card-content">
                <h3>VEHICLE</h3>
                <p className="card-value">N/A</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {isLoading ? (
          <>
            {driverMetrics.map((metric) => (
              <div key={metric.id} className="chart-card skeleton">
                <div className="skeleton-title"></div>
                <div className="skeleton-chart"></div>
              </div>
            ))}
          </>
        ) : error ? (
          <div className="error-state-container">
            <div className="error-state">
              <NativeSvgIcon name="add" />
              <h3>Unable to Load Data</h3>
              <p>We couldn't retrieve the driver score metrics. Please try again later.</p>
            </div>
          </div>
        ) : (
          <>
            {driverMetrics.map((metric) => {
              const metricData = getMetricChartData(metric.id);
              const chartData = {
                labels: Object.values(pieChartLegends).map(legend => legend.label),
                datasets: [
                  {
                    data: metricData.data,
                    backgroundColor: Object.values(pieChartLegends).map(legend => legend.color),
                    borderWidth: 0,
                  },
                ],
              };

              return (
                <div key={metric.id} className="chart-card">
                  <h4 className="chart-title">{metric.label}</h4>
                  <div className="chart-container">
                    <Pie data={chartData} options={chartOptions} />
                  </div>
                </div>
              );
            })}
            {/* Legend */}
            <div className="chart-legend">
              {pieChartLegends && Object.values(pieChartLegends).map((legend, index) => (
                <div key={`legend-${index}`} className="legend-item">
                  <span className="legend-color" style={{ backgroundColor: legend.color }}></span>
                  <span className="legend-label">{legend.label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Driver Score Section */}
      <div className="driver-score-section">
        {isLoading ? (
          <>
            {[1, 2, 3].map((index) => (
              <div key={index} className="score-card skeleton">
                <div className="skeleton-donut"></div>
                <div className="skeleton-label"></div>
              </div>
            ))}
          </>
        ) : (
          driverScoreCategories.map((category) => (
            <div key={category.id} className="score-card">
              <div className="score-chart-container">
                <Doughnut
                  data={createDonutData(category.percentage, category.color)}
                  options={donutOptions}
                />
                <div className="score-percentage">{category.percentage}%</div>
              </div>
              <p className="score-label">{category.label}</p>
            </div>
          ))
        )}
      </div>

      {/* Copyright */}
      <div className="copyright">
        Copyright @ 2025 PT Quantum Inventions Indonesia
      </div>
      </div>
    </div>
  );
};
